import { startTransition } from 'react';
import { logClientError } from './client-error-logger';
import { traceUpdate, justResumedFromHidden, justLoaded } from './render-update-tracer';

// Gom các lần setState từ listener `onSnapshot` DÙNG CHUNG toàn app (planFeatures/todoCounts/bảng todos/
// badge chat…) thành MỘT commit ở mức ưu tiên thấp (transition).
//
// VÌ SAO (2026-10-10, React #185 "Maximum update depth exceeded" — ca thật Nắng Home /todos sập lặp ~20 lần
// trong 15' với 1 tài khoản, `updateTrace` lần thứ 2 không rỗng: planFeatures + todo-counts + todos-table +
// unread-chats cùng bắn): mỗi listener Firestore giao snapshot ở 1 TÁC VỤ riêng ⇒ mỗi `setState` là 1 lần
// render riêng của provider + mọi component dùng context đó. React 19 tính các commit nối tiếp còn treo lane
// Default là "nested update" (react-admin #11392) nên nhiều listener độc lập bắn sát nhau có thể cộng dồn
// vượt trần 50 dù KHÔNG component nào lặp thật. Cách giảm không cần biết gốc chắc chắn: (1) dồn mọi cập nhật
// trong ~1 khung hình thành 1 commit, (2) đẩy sang lane transition — không tính vào bộ đếm nested của
// Sync/Default. Đây là giảm thiểu theo cơ chế đã công bố, CHƯA phải bằng chứng đã tìm ra thủ phạm.
//
// Quy ước: mỗi NGUỒN cập nhật dùng 1 `key` riêng (hook có thể mount nhiều bản → key kèm `useId()`); cập nhật
// sau cùng của cùng key trong cùng cửa sổ THẮNG (state là giá trị thay thế toàn phần, không phải delta).
// Cleanup effect PHẢI gọi `dropSharedUpdate(key)` để không áp snapshot của listener đã huỷ (đổi uid/tenant).
const BATCH_MS = 16;
// "Dồn cục lúc hồi từ nền" (2026-10-11, xem render-update-tracer.ts): iOS Safari treo timer/kết nối khi tab
// bị đưa xuống nền; lúc hồi, Firestore giao 1 cục backlog cho các listener gần như cùng lúc nhưng KHÔNG
// nhất thiết gọn trong 16ms — cửa sổ 16ms có thể vỡ cục đó thành nhiều lần flush liên tiếp (mỗi lần vẫn
// tính 1 nested update). Trong ~RESUME_GRACE_MS sau khi tab vừa hiện trở lại, nới cửa sổ gom lên
// RESUME_BATCH_MS để có cơ hội gom TRỌN cục vào 1 flush — đánh đổi độ trễ hiển thị (chấp nhận được, vì
// đây đúng lúc user vừa quay lại, chưa kịp nhìn) lấy giảm số lần commit dồn dập.
const RESUME_BATCH_MS = 400;
const RESUME_GRACE_MS = 2000;
// "Dồn cục ngay sau khi vừa tải trang" (2026-10-11, bằng chứng THẬT từ chính user tự tái hiện): KHÔNG
// phải tab hồi từ nền (`lastHiddenForMs` luôn rỗng ở các ca có updateTrace) — mà là `switchWorkspace()`
// (`lib/auth.tsx`) làm hard reload `window.location.href`, nạp lại cả trang → 4 hook dùng chung mount
// LẦN ĐẦU cùng lúc, mỗi cái tự nhận snapshot Firestore ĐẦU TIÊN của mình gần như đồng thời. Cùng cơ chế
// với "dồn cục lúc resume" nhưng khác lý do (mount mới, không phải backlog tích luỹ) — cùng cách giảm:
// nới cửa sổ gom ngay sau khi trang vừa tải, bất kể F5 thường hay hard reload sau switchWorkspace/impersonate.
//
// 2500ms KHÔNG ĐỦ cho cold-load chậm (2026-10-26, nanghome, 6 lần crash /todos trong <30s, lần đầu bắt
// được `updateTrace` ĐẦY ĐỦ 16 sự kiện): cascade boot thật kéo dài ~1566ms nhưng bắt đầu ~1088ms sau khi
// trang hiện (visibilitychange) — straggler cuối (`shared-batch:1`) rơi ở mốc ~2654ms sau khi hiện, TỨC
// NGAY SAU khi hết hạn ân hạn 2500ms → tách thành 1 commit riêng thay vì gộp cùng nhóm trước đó
// (`shared-batch:8`), đúng cơ chế mà cửa sổ ân hạn này sinh ra để tránh. Nới lên 4000ms cho dư biên độ
// mạng yếu/cache nguội — KHÔNG phải bằng chứng đã hết bí ẩn #185 (vẫn còn trace rỗng ở nhiều ca khác),
// chỉ là thu hẹp 1 cơ chế góp phần đã đo được trực tiếp.
const LOAD_GRACE_MS = 4000;
const pending = new Map<string, () => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

function flush(): void {
  timer = null;
  const fns = [...pending.values()];
  pending.clear();
  if (fns.length === 0) return;
  traceUpdate(`shared-batch:${fns.length}`);
  startTransition(() => {
    for (const apply of fns) {
      try { apply(); }
      catch (e) {
        logClientError({ route: 'lib/shared-update-batcher.ts', action: 'apply_shared_update', message: e instanceof Error ? e.message : String(e) });
      }
    }
  });
}

/** Xếp 1 lần `setState` của listener dùng chung vào cửa sổ gom ~16ms (transition) — nới lên
 *  `RESUME_BATCH_MS` nếu tab vừa hồi từ nền HOẶC trang vừa tải trong cửa sổ ân hạn tương ứng. */
export function queueSharedUpdate(key: string, apply: () => void): void {
  pending.set(key, apply);
  const wide = justResumedFromHidden(RESUME_GRACE_MS) || justLoaded(LOAD_GRACE_MS);
  timer ??= setTimeout(flush, wide ? RESUME_BATCH_MS : BATCH_MS);
}

/** Bỏ cập nhật đang chờ của `key` — gọi trong cleanup effect khi huỷ listener. */
export function dropSharedUpdate(key: string): void {
  pending.delete(key);
}
